export const SETTINGS_KEY = 'augment-hr.settings';

export const COLOR_PRESETS = {
  dogwoodRose: {
    label: 'Dogwood Rose',
    primary: '#DC0069',
    primaryLight: '#F8B3D1',
    primaryLightest: '#FCE8F0',
    primaryDark: '#9A0048',
    primaryDarkest: '#5A002A',
  },
  claret: {
    label: 'Claret',
    primary: '#6E0028',
    primaryLight: '#E6B3C0',
    primaryLightest: '#F5E8EB',
    primaryDark: '#4A001B',
    primaryDarkest: '#2D0010',
  },
  byzantium: {
    label: 'Byzantium',
    primary: '#820064',
    primaryLight: '#E6B3D1',
    primaryLightest: '#F5E8F0',
    primaryDark: '#5A0045',
    primaryDarkest: '#330028',
  },
};

export const DEFAULT_SETTINGS = {
  brand: 'augment',
  colorPreset: 'dogwoodRose',
  paletteMode: 'light',
  navColor: 'blend-in',
  contrast: 'high',
};

// Switchable brand identities. `augment` = the existing system (unchanged);
// `dih` = the Digital Infrastructure Holdings client theme — palette from
// docs/DIH_Brand_Guidelines.pdf, logo/icon from docs/DIH Logo 1 / DP Icon.
export const BRANDS = {
  augment: {
    label: 'Augment',
    logo: '/assets/augment-logo/augment-hr-lockup.svg',
    // Square mark for the browser tab — the wide wordmark above renders as an
    // illegible sliver at 16px. (`logo` is the in-app header; `icon` is the favicon.)
    icon: '/favicon1.svg',
    name: 'Augment HR',
  },
  dih: {
    label: 'DIH',
    logo: '/assets/dih-logo/dih-logo.svg',
    icon: '/assets/dih-logo/dih-icon.svg',
    name: 'DIH',
  },
};

export function isDihBrand(settings) {
  return settings?.brand === 'dih';
}

// Arial-always per the DIH guidelines; Augment keeps Inter.
const DIH_FONT = 'Arial, Helvetica, sans-serif';
const AUGMENT_FONT = "'Inter', ui-sans-serif, system-ui, sans-serif";

// Full DIH token set (Petrol primary, Teal accent, navy ink, neutral-dominant).
const DIH_TOKENS = {
  primary: '#005677',        // Petrol Blue
  primaryLight: '#4D8DA5',
  primaryLightest: '#E2EEF2',
  primaryDark: '#004864',
  primaryDarkest: '#003D52',
  secondary: '#3AC1CD',      // DIH Teal (hero accent)
  secondaryLight: '#7FD8DF',
  secondaryLightest: '#E6F7F9',
  secondaryDark: '#0B6D77',
  // Categorical accent system maps onto the existing "purple" tertiary slot →
  // teal, so any purple-based accent renders on-brand under DIH.
  purple: '#3AC1CD',
  purpleLightest: '#E6F7F9',
  purpleDark: '#0B6D77',
  blue: '#005677',
  success: '#1A8A5C',
  successDark: '#15724B',
  warning: '#F99C24',        // Warm Orange
  warningDark: '#9A6512',
  error: '#D02A2F',          // Brand Red
  errorDark: '#A52025',
  info: '#3AC1CD',
  infoDark: '#0B6D77',
  text: '#1E293B',           // Body text / Deep Navy
  textSec: '#64748B',        // Slate Muted
  divider: '#E2E8F0',
  paper: '#FFFFFF',
  bg: '#F7F9FC',             // Page Tint
  n50: '#F7F9FC',
  n100: '#E8ECF1',           // Card Fill
  n200: '#E2E8F0',
  n300: '#CBD5E1',
  n400: '#8896A6',           // Faint Slate
  n500: '#64748B',
  n600: '#475569',
  n700: '#334155',
  n800: '#1E293B',
  n900: '#1E293B',
};

// DIH dark surfaces (navy), reusing the same accents.
const DIH_DARK_SURFACES = {
  text: '#E2E8F0',
  textSec: '#94A3B8',
  divider: '#334155',
  paper: '#1E293B',
  bg: '#0F1729',
  n50: '#0F1729',
  n100: '#1E293B',
  n200: '#334155',
  n300: '#475569',
  n600: '#CBD5E1',
  n900: '#F1F5F9',
};

// DIH sidebar: light, petrol-accented (keeps the petrol logo readable).
const DIH_NAV = {
  light: {
    '--nav-bg': '#FFFFFF',
    '--nav-border-color': '#E2E8F0',
    '--nav-section-title-color': '#8896A6',
    '--nav-item-color': '#475569',
    '--nav-item-hover-bg': 'rgba(0, 86, 119, 0.06)',
    '--nav-item-active-bg': '#E2EEF2',
    '--nav-item-active-color': '#005677',
    '--nav-item-icon-color': '#8896A6',
    '--nav-item-icon-active-color': '#005677',
    '--nav-item-chevron-color': '#8896A6',
  },
  dark: {
    '--nav-bg': '#0B1120',
    '--nav-border-color': '#1E293B',
    '--nav-section-title-color': '#64748B',
    '--nav-item-color': '#94A3B8',
    '--nav-item-hover-bg': 'rgba(58, 193, 205, 0.10)',
    '--nav-item-active-bg': 'rgba(58, 193, 205, 0.16)',
    '--nav-item-active-color': '#FFFFFF',
    '--nav-item-icon-color': '#64748B',
    '--nav-item-icon-active-color': '#3AC1CD',
    '--nav-item-chevron-color': '#64748B',
  },
};

function hexToRgb(hex) {
  const h = String(hex).replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(v, 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

export function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

export function isCustomSettings(settings) {
  return Object.keys(DEFAULT_SETTINGS).some((k) => settings[k] !== DEFAULT_SETTINGS[k]);
}

const SURFACES = {
  light: { high: { bg: '#F8F9FA', paper: '#FFFFFF' }, normal: { bg: '#FFFFFF', paper: '#FFFFFF' } },
  dark: { high: { bg: '#0B0F19', paper: '#111927' }, normal: { bg: '#0E1320', paper: '#111927' } },
};

const NAV_VARS = {
  'blend-in': {
    light: {
      '--nav-bg': '#FFFFFF',
      '--nav-border-color': '#F3F4F6',
      '--nav-section-title-color': '#9DA4AE',
      '--nav-item-color': '#6C737F',
      '--nav-item-hover-bg': 'rgba(17, 25, 39, 0.04)',
      '--nav-item-active-bg': '#F3F4F6',
      '--nav-item-active-color': '#111927',
      '--nav-item-icon-color': '#9DA4AE',
      '--nav-item-icon-active-color': null,
    },
    dark: {
      '--nav-bg': '#0B0F19',
      '--nav-border-color': '#2F3746',
      '--nav-section-title-color': '#9DA4AE',
      '--nav-item-color': '#9DA4AE',
      '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-color': '#FFFFFF',
      '--nav-item-icon-color': '#6C737F',
      '--nav-item-icon-active-color': null,
    },
  },
  discrete: {
    light: {
      '--nav-bg': '#F8F9FA',
      '--nav-border-color': '#F2F4F7',
      '--nav-section-title-color': '#6C737F',
      '--nav-item-color': '#6C737F',
      '--nav-item-hover-bg': 'rgba(17, 25, 39, 0.04)',
      '--nav-item-active-bg': '#F3F4F6',
      '--nav-item-active-color': '#111927',
      '--nav-item-icon-color': '#9DA4AE',
      '--nav-item-icon-active-color': null,
    },
    dark: {
      '--nav-bg': '#111927',
      '--nav-border-color': '#2F3746',
      '--nav-section-title-color': '#9DA4AE',
      '--nav-item-color': '#9DA4AE',
      '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-color': '#FFFFFF',
      '--nav-item-icon-color': '#6C737F',
      '--nav-item-icon-active-color': null,
    },
  },
  evident: {
    light: {
      '--nav-bg': '#1C2536',
      '--nav-border-color': 'transparent',
      '--nav-section-title-color': '#9DA4AE',
      '--nav-item-color': '#9DA4AE',
      '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-color': '#FFFFFF',
      '--nav-item-icon-color': '#9DA4AE',
      '--nav-item-icon-active-color': null,
    },
    dark: {
      '--nav-bg': '#1C2536',
      '--nav-border-color': 'transparent',
      '--nav-section-title-color': '#9DA4AE',
      '--nav-item-color': '#9DA4AE',
      '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
      '--nav-item-active-color': '#FFFFFF',
      '--nav-item-icon-color': '#9DA4AE',
      '--nav-item-icon-active-color': null,
    },
  },
};

export function getPaletteTokens(settings) {
  const mode = settings.paletteMode === 'dark' ? 'dark' : 'light';
  const isDark = mode === 'dark';

  // ---- DIH brand: fixed institutional identity (preset/nav/contrast N/A) ----
  if (isDihBrand(settings)) {
    const t = isDark ? { ...DIH_TOKENS, ...DIH_DARK_SURFACES } : { ...DIH_TOKENS };
    return { ...t, font: DIH_FONT };
  }

  // ---- Augment brand: existing behaviour, unchanged ----
  const preset = COLOR_PRESETS[settings.colorPreset] || COLOR_PRESETS.dogwoodRose;
  const contrast = settings.contrast === 'normal' ? 'normal' : 'high';
  const surfaces = SURFACES[mode][contrast];

  return {
    ...preset,
    secondary: '#06AED4',
    secondaryLight: '#7FD8F0',
    secondaryLightest: '#E0F7FF',
    secondaryDark: '#0E7090',
    font: AUGMENT_FONT,
    bg: surfaces.bg,
    paper: surfaces.paper,
    text: isDark ? '#EDF2F7' : '#111927',
    textSec: isDark ? '#9DA4AE' : '#6C737F',
    divider: isDark ? '#2D3748' : '#F2F4F7',
    n50: isDark ? '#111927' : '#F8F9FA',
    n100: isDark ? '#1C2536' : '#F3F4F6',
    n200: isDark ? '#2F3746' : '#E5E7EB',
    n400: '#9DA4AE',
    n500: '#6C737F',
    n600: isDark ? '#9DA4AE' : '#4D5761',
    n900: isDark ? '#EDF2F7' : '#111927',
  };
}

// Chart series colours per brand. Augment: magenta→cyan→amber→purple; DIH uses
// the fixed categorical cycle Petrol→Teal→Orange→Green (+ slate).
export function getPieColors(settings) {
  const t = getPaletteTokens(settings);
  if (isDihBrand(settings)) {
    return ['#005677', '#3AC1CD', '#F99C24', '#1A8A5C', '#8896A6'];
  }
  return [t.primary, '#06AED4', '#F79009', '#9E77ED', '#9DA4AE'];
}

export function applyAppSettings(settings) {
  const root = document.documentElement;
  const tokens = getPaletteTokens(settings);
  const brand = isDihBrand(settings) ? 'dih' : 'augment';
  const mode = settings.paletteMode === 'dark' ? 'dark' : 'light';
  // DIH carries its own sidebar; Augment keeps the user-selectable nav presets.
  const nav =
    brand === 'dih'
      ? DIH_NAV[mode]
      : NAV_VARS[settings.navColor]?.[mode] || NAV_VARS['blend-in'][mode];

  root.setAttribute('data-brand', brand);
  root.setAttribute('data-palette-mode', mode);
  root.setAttribute('data-nav-color', brand === 'dih' ? 'dih' : settings.navColor || 'blend-in');
  root.setAttribute('data-contrast', settings.contrast || 'high');

  const vars = {
    '--app-font': tokens.font,
    '--primary': tokens.primary,
    '--primary-dark': tokens.primaryDark,
    '--primary-darkest': tokens.primaryDarkest,
    '--primary-light': tokens.primaryLight,
    '--primary-lightest': tokens.primaryLightest,
    '--primary-rgb': hexToRgb(tokens.primary),
    '--secondary': tokens.secondary,
    '--secondary-dark': tokens.secondaryDark,
    '--secondary-lightest': tokens.secondaryLightest,
    '--accent': tokens.purple,
    '--background': tokens.bg,
    '--paper': tokens.paper,
    '--text': tokens.text,
    '--text-secondary': tokens.textSec,
    '--divider': tokens.divider,
    '--neutral-50': tokens.n50,
    '--neutral-100': tokens.n100,
    '--neutral-200': tokens.n200,
    ...nav,
  };

  Object.entries(vars).forEach(([key, value]) => {
    if (value != null) root.style.setProperty(key, value);
  });
  // Augment's nav presets leave this null (→ primary); DIH sets it explicitly.
  root.style.setProperty('--nav-item-icon-active-color', nav['--nav-item-icon-active-color'] || tokens.primary);

  document.body.style.backgroundColor = tokens.bg;
  document.body.style.color = tokens.text;
  document.body.style.fontFamily = tokens.font;

  // Brand favicon
  const favicon = document.querySelector('link[rel~="icon"]');
  if (favicon) favicon.href = BRANDS[brand].icon;

  // Brand tab title — keep it in sync with the active brand so DIH users
  // don't see "Augment HR" in the browser tab.
  const brandName = BRANDS[brand].name;
  document.title = /\bHR\b/.test(brandName) ? brandName : `${brandName} HR`;
}
